import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { flushSync } from 'react-dom'
import { AuthState, LoginCredentials, RegisterCredentials, User } from '../types/auth'
import { apiClient } from '../services/apiClient'

export function useAuth(): AuthState & {
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  register: (credentials: RegisterCredentials) => Promise<void>
  refreshUser: () => Promise<void>
  getRefreshAttempts: () => number
  resetRefreshAttempts: () => void
} {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Check for existing auth token on mount
    const checkAuth = async () => {
      try {
        // First check if we have any tokens at all
        if (!apiClient.isAuthenticated()) {
          // No tokens, check if we have stale cached user data to clear
          const cachedUser = apiClient.getCachedUser();
          if (cachedUser) {
            console.log('🧹 No tokens but found cached user data, clearing stale data');
            localStorage.removeItem('firedup_user');
          }
          setLoading(false);
          return;
        }

        // We have tokens, but check if we've hit the refresh attempt limit
        const refreshAttempts = apiClient.getRefreshAttempts();
        if (refreshAttempts >= 3) {
          console.log('🔒 Too many refresh attempts, clearing auth state without retry');
          apiClient.resetRefreshAttempts();
          await apiClient.logout();
          setUser(null);
          setLoading(false);
          return;
        }

        // Try to validate the token (this might trigger a refresh if needed)
        try {
          // Use the no-refresh validation first to avoid infinite loops
          const validateResponse = await apiClient.validateTokenNoRefresh();
          if (validateResponse.valid && validateResponse.user) {
            setUser(validateResponse.user);
            // Reset refresh attempts on successful validation
            apiClient.resetRefreshAttempts();
            // Update cached user data
            localStorage.setItem('firedup_user', JSON.stringify(validateResponse.user));
          } else {
            // Token is invalid, try a normal validation which might attempt refresh
            console.log('🔄 Initial validation failed, attempting with refresh...');
            try {
              const retryValidation = await apiClient.validateToken();
              if (retryValidation.valid && retryValidation.user) {
                setUser(retryValidation.user);
                apiClient.resetRefreshAttempts();
                localStorage.setItem('firedup_user', JSON.stringify(retryValidation.user));
              } else {
                console.log('🔒 Token validation failed after refresh, clearing auth state');
                await apiClient.logout();
                setUser(null);
              }
            } catch (refreshError) {
              console.log('🔒 Token refresh failed, clearing auth state');
              await apiClient.logout();
              setUser(null);
            }
          }
        } catch (tokenError) {
          // If validation fails, use cached user temporarily but don't retry validation
          console.log('🔒 Token validation failed, using cached user if available');
          const cachedUser = apiClient.getCachedUser();
          if (cachedUser) {
            console.log('📋 Using cached user data until next page refresh');
            setUser(cachedUser);
          } else {
            await apiClient.logout();
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        
        // Check if this was due to multiple failed refresh attempts
        const attempts = apiClient.getRefreshAttempts();
        if (attempts > 0) {
          console.error(`❌ Authentication failed after ${attempts} refresh attempts. Please log in again.`);
        }
        
        // Clear all auth data on error
        await apiClient.logout();
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    checkAuth()
  }, [])

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setLoading(true)
    try {
      const response = await apiClient.login(credentials);
      setUser(response.user);
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    setLoading(true)
    try {
      const response = await apiClient.register(credentials);
      setUser(response.user);
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    console.log('🔓 Logout initiated - Current user:', user?.displayName);
    
    try {
      await apiClient.logout();
      console.log('✅ Server logout successful');
    } catch (error) {
      // Even if server logout fails, we still want to clear local state
      console.warn('⚠️ Server logout failed:', error);
    }
    
    // Use flushSync to ensure state update is applied immediately
    flushSync(() => {
      setUser(null);
    });
    
    console.log('🔄 User state cleared, navigating to home...');
    
    // Navigate to home page after state is cleared
    navigate('/');
  }

  const refreshUser = async (): Promise<void> => {
    try {
      const validateResponse = await apiClient.validateToken();
      if (validateResponse.valid && validateResponse.user) {
        setUser(validateResponse.user);
        apiClient.resetRefreshAttempts(); // Reset on successful refresh
        localStorage.setItem('firedup_user', JSON.stringify(validateResponse.user));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }

  const getRefreshAttempts = (): number => {
    return apiClient.getRefreshAttempts();
  }

  const resetRefreshAttempts = (): void => {
    apiClient.resetRefreshAttempts();
  }

  return {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    register,
    refreshUser,
    getRefreshAttempts,
    resetRefreshAttempts
  }
}
